/**
 * ContractRequiredBanner — yellow inline warning shown on PENDING_DEPOSIT
 * loans that don't yet have a contract row.
 *
 * Migration 0030 added the `loans_contract_required_when_active` CHECK
 * constraint, so any transition past PENDING_DEPOSIT (including the
 * automatic PENDING_DEPOSIT → ACTIVE flip from a settled deposit
 * payment) fails at the DB level when contract_id is NULL. Without this
 * banner the operator would record a deposit, hit a confusing 500 /
 * DepositAmountMismatch-shaped error, and not understand why.
 *
 * The banner is intentionally non-blocking — the operator might still
 * legitimately record the payment to capture the receipt before the
 * contract is signed (the loan stays PENDING_DEPOSIT until both clear).
 * The deposit form's submit button disables when this state is true so
 * the operator can't accidentally trigger the CHECK error.
 */
import { Link } from "@tanstack/react-router";
import { Card, CardBody } from "./Card.js";

interface Props {
  readonly loanId: string;
}

export function ContractRequiredBanner(props: Props): JSX.Element {
  return (
    <Card>
      <CardBody className="bg-amber-50 border-l-4 border-amber-400">
        <div className="flex items-start gap-3">
          <span aria-hidden className="text-2xl leading-none">
            ⚠
          </span>
          <div className="flex-1 space-y-1">
            <p className="text-sm font-semibold text-amber-900">
              Sign the contract before recording the deposit
            </p>
            <p className="text-xs text-amber-800 leading-snug">
              This loan has no contract on file yet. The deposit payment will not activate the loan
              until the contract is signed (the database refuses to flip a loan to ACTIVE without
              one).
            </p>
            <div className="pt-1">
              <Link
                to="/loans/$id/sign-contract"
                params={{ id: props.loanId }}
                className="inline-block rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Sign contract →
              </Link>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
